import React, { useEffect, useState, useCallback } from "react";
import { useProjects } from "../context/ProjectContext";
import api, { apiError } from "../lib/api";
import { Loader2, Table2, Braces, Copy } from "lucide-react";
import { EmptyState } from "../components/EmptyState";
import { toast } from "sonner";

// Read-only view of the raw synced Procore budget data.
const FIELD_LABELS = {
  cost_code: "Cost Code", description: "Description", uom: "UOM",
  std_rate: "Std $/hr", orig_qty: "Orig Qty", rev_qty: "Rev Qty",
  orig_hrs: "Orig Hrs", rev_hrs: "Rev Hrs", installed_qty: "Installed Qty",
  sage_hrs: "Sage hrs",
};

export default function RawProcoreData() {
  const { selectedId, selected } = useProjects();
  const [view, setView] = useState("budget");
  const [rawMode, setRawMode] = useState("kv");
  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState(null);
  const [budget, setBudget] = useState(null);

  const load = useCallback(async () => {
    if (!selectedId) { setLoading(false); return; }
    setLoading(true);
    const [rawData, budgetData] = await Promise.all([
      api.get(`/projects/${selectedId}/procore/raw-rows`).then((r) => r.data).catch(() => ({ rows: [] })),
      api.get(`/projects/${selectedId}/procore/budget-lines`).then((r) => r.data).catch(() => ({ lines: [], mapping: {}, app_fields: [] })),
    ]);
    setRaw(rawData); setBudget(budgetData); setLoading(false);
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  if (!selectedId) return <EmptyState title="No project selected" hint="Pick a project from the top bar to view its synced Procore data." />;
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;

  const mapping = budget?.mapping || {};
  const appFields = budget?.app_fields || Object.keys(FIELD_LABELS);
  const lines = budget?.lines || [];
  const rows = raw?.rows || [];
  const lastSynced = raw?.synced_at;

  const cellValue = (line, field) => {
    const key = mapping[field];
    if (!key) { if (field === "cost_code") return line.cost_code; return ""; }
    const v = (line.combined || {})[key];
    if (v === null || v === undefined) return "";
    return typeof v === "object" ? JSON.stringify(v) : String(v);
  };

  const SKIP_KEYS = new Set(["segment_items"]);
  const rowLabel = (row) => row.flat_code || (row.wbs_code && row.wbs_code.flat_code) || row.budget_code || `row ${row.id ?? ""}`;
  const rowKV = (row) => Object.entries(row).filter(([k]) => !SKIP_KEYS.has(k))
    .map(([k, v]) => `${k}: ${v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : v}`).join("\n");
  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  return (
    <div className="space-y-4" data-testid="raw-procore-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold text-slate-900">Procore Data</h1>
          <p className="text-sm text-slate-500">
            {selected?.project_number} — {selected?.project_name}
            {lastSynced ? ` · last synced ${new Date(lastSynced).toLocaleString()}` : " · not synced yet"}
            <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Read-only</span>
          </p>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
          <button onClick={() => setView("budget")} data-testid="tab-budget-lines"
            className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${view === "budget" ? "bg-[#0B192C] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            <Table2 className="w-4 h-4" />Budget Lines ({lines.length})
          </button>
          <button onClick={() => setView("raw")} data-testid="tab-raw-json"
            className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${view === "raw" ? "bg-[#0B192C] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            <Braces className="w-4 h-4" />Raw JSON ({rows.length})
          </button>
        </div>
      </div>

      {view === "budget" ? (
        lines.length === 0 ? (
          <EmptyState title="No budget lines" hint="Click 'Sync data' on this project (Projects page) to pull the Procore Standard Budget." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="budget-lines-table">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap border-b border-slate-200">Type</th>
                  {appFields.map((f) => <th key={f} className="px-3 py-2 font-semibold whitespace-nowrap border-b border-slate-200">{FIELD_LABELS[f] || f}</th>)}
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={`${line.cost_code}-${line.cost_type}`} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`budget-line-${line.cost_code}-${line.cost_type}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs font-semibold text-slate-600">{line.cost_type || ""}</td>
                    {appFields.map((f) => <td key={f} className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-700">{cellValue(line, f) || <span className="text-slate-300">—</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        rows.length === 0 ? (
          <EmptyState title="No raw rows" hint="Sync this project to store the full raw JSON of every Procore budget row." />
        ) : (
          <div className="space-y-3">
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
              <button onClick={() => setRawMode("kv")} data-testid="rawmode-kv"
                className={`px-3 py-1 font-medium ${rawMode === "kv" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Key: value (top-level)</button>
              <button onClick={() => setRawMode("json")} data-testid="rawmode-json"
                className={`px-3 py-1 font-medium ${rawMode === "json" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Full JSON</button>
            </div>
            {rawMode === "json" ? (
              <div className="bg-[#0B192C] text-[#C7D6E8] rounded-md shadow-sm p-4 overflow-x-auto max-h-[70vh]" data-testid="raw-json-block">
                <pre className="text-xs leading-relaxed"><code>{JSON.stringify(rows, null, 2)}</code></pre>
              </div>
            ) : (
              <div className="space-y-3 max-h-[72vh] overflow-y-auto pr-1" data-testid="raw-kv-block">
                <p className="text-xs text-slate-500">Top-level keys only, <span className="font-medium">segment_items</span> excluded. One "key: value" per line.</p>
                {rows.map((row, i) => {
                  const text = rowKV(row);
                  return (
                    <div key={i} className="bg-white border border-slate-200 rounded-md shadow-sm" data-testid={`raw-kv-row-${rowLabel(row)}`}>
                      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                        <span className="font-mono text-xs font-semibold text-slate-800">{rowLabel(row)}</span>
                        <button onClick={() => copyText(text)} data-testid={`copy-kv-${rowLabel(row)}`}
                          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#E8791A]"><Copy className="w-3.5 h-3.5" />Copy</button>
                      </div>
                      <pre className="text-xs leading-relaxed text-slate-700 whitespace-pre-wrap break-words p-3 font-mono">{text}</pre>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
